import React, { useCallback, useState } from 'react';
import {
  Box,
  Btn,
  ConnectedDiv,
  Desc,
  Description,
  DetailsContainer,
  LeftContent,
  Logo,
  RightContent,
  Tag,
  TagBtn,
  Title,
  TitleContainer,
  View
} from '../integrationBox/styles';
import Button from '../button';
import { REACT_APP_FIGMA_OAUTH_URL, REACT_APP_SENTRY_URL } from '../../global/environment';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../routes/urls';
import { captureException } from '../../services/logService';
import { disconnectFigmaAccount, disconnectStripe } from '../../services/settingServices';
import { useMobileDevice } from '../../global/useMobile';
import { BUTTON_TYPE } from '../../global/constants';
import Image, { ImageKey } from '../image';

interface Props {
  title: string;
  description: string;
  isConnect?: boolean;
  isConnected?: boolean;
  isComingSoonTag?: boolean;
  isLearnMoreBtn?: boolean;
  isLast?: boolean;
  loadData: Function;
  IntegrationsLogoName?: ImageKey;
}

export default function IntegrationBox(props: Props) {
  //State
  const [loading, setLoading] = useState(false);

  //Other variable
  const history = useHistory();
  const isMobile = useMobileDevice();

  // connect with figma
  const figmaConnect = useCallback(() => {
    try {
      setLoading(true);
      const newTab = window.open(REACT_APP_FIGMA_OAUTH_URL, '_blank');
      if (newTab) {
        newTab.focus();
      } else {
        alert('Please allow pop-ups for this site to open in a new tab.');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // connect with figma
  const sentryConnect = useCallback(() => {
    try {
      setLoading(true);
      const newTab = window.open(REACT_APP_SENTRY_URL, '_blank');
      if (newTab) {
        newTab.focus();
      } else {
        alert('Please allow pop-ups for this site to open in a new tab.');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Disconnect with figma
  const sentryDisconnect = useCallback(() => {
    try {
      setLoading(true);
      const newTab = window.open('https://sentry.io/settings', '_blank');
      if (newTab) {
        newTab.focus();
      } else {
        alert('Please allow pop-ups for this site to open in a new tab.');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // disconnect from figma
  const figmaDisconnect = useCallback(async () => {
    const result = await disconnectFigmaAccount();
    if (result) {
      props?.loadData();
    }
  }, [props]);

  // disconnect from stripe
  const stripeDisconnect = useCallback(async () => {
    const result = await disconnectStripe();
    if (result) {
      props?.loadData();
    }
  }, [props]);

  // connect with different platform
  const onClickConnect = useCallback(() => {
    if (props?.title === 'Figma') {
      figmaConnect();
    } else if (props?.title === 'Github') {
      history.push(ROUTES.INTEGRATIONS_DETAILS);
    } else if (props?.title === 'Sentry') {
      sentryConnect();
    } else if (props?.title === 'Vercel') {
      history.push(ROUTES.VERCEL_INTEGRATION_DETAILS);
    }
  }, [figmaConnect, history, props?.title, sentryConnect]);

  // redirect on learn more about platform
  const onClickLearnMore = useCallback(() => {
    if (props?.title === 'Stripe') {
      history.push(ROUTES.PAYMENT_SETTING);
    } else if (props?.title === 'Zapier') {
      window.open('https://zapier.com/apps/teamcamp/integrations', '_blank', 'noopener,noreferrer');
    } else if (props?.title === 'Api') {
      window.open('https://api.teamcamp.app', '_blank', 'noopener,noreferrer');
    }
  }, [history, props?.title]);

  // disconnect from different platform
  const onClickDisconnect = useCallback(() => {
    try {
      setLoading(true);
      if (props?.title === 'Figma') figmaDisconnect();
      else if (props?.title === 'Stripe') stripeDisconnect();
      else if (props?.title === 'Sentry') sentryDisconnect();
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, [figmaDisconnect, props?.title, sentryDisconnect, stripeDisconnect]);

  // render right side content in the card
  const renderRightContent = (
    <RightContent>
      {props.isConnect && (
        <Button
          title='Connect'
          variant={BUTTON_TYPE.SECONDARY}
          size='small'
          onClick={onClickConnect}
          disabled={loading}
        />
      )}
      {props.isConnected &&
        (!isMobile ? (
          <ConnectedDiv>
            <Tag className='connect-button'>Connected</Tag>
            <View className='disconnect-button'>
              <Button
                title='Disconnect'
                variant={BUTTON_TYPE.SECONDARY}
                size='small'
                onClick={onClickDisconnect}
                disabled={loading}
                isLoading={loading}
              />
            </View>
          </ConnectedDiv>
        ) : (
          <View>
            <Button
              title='Disconnect'
              variant={BUTTON_TYPE.SECONDARY}
              size='small'
              onClick={onClickDisconnect}
              disabled={loading}
              isLoading={loading}
            />
          </View>
        ))}
      {props.isComingSoonTag && <TagBtn>Coming soon</TagBtn>}
      {props.isLearnMoreBtn && (
        <Btn role='button' onClick={onClickLearnMore}>
          Learn more
        </Btn>
      )}
    </RightContent>
  );

  return (
    <>
      <Box isLast={props.isLast}>
        <LeftContent>
          <Logo>
            <Image imageKey={props.IntegrationsLogoName} alt={props.title} width={32} height={32} />
          </Logo>
          <DetailsContainer>
            <Description>
              <TitleContainer>
                <Title>{props.title}</Title>
                {props.isConnected && isMobile && <Tag className='connect-button'>Connected</Tag>}
              </TitleContainer>
              <Desc>{props.description}</Desc>
            </Description>
            {isMobile && renderRightContent}
          </DetailsContainer>
        </LeftContent>
        {!isMobile && renderRightContent}
      </Box>
    </>
  );
}
